import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import { useBxpWebStargateClient } from "@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client";
import { FormattedMessage, useIntl } from "react-intl";

import { IconButton } from "@atlaskit/button-next/new";
import Heading from "@atlaskit/heading";
import CrossIcon from "@atlaskit/icon/core/cross";
import { Box, Inline, Stack, Text, xcss } from "@atlaskit/primitives-next";
import SectionMessage, {
    SectionMessageAction
} from "@atlaskit/section-message";
import Spinner from "@atlaskit/spinner";
import Tooltip from "@atlaskit/tooltip";

import { useFastshiftDispatch, useFastshiftSelector } from "@/state";
import { getFastshiftRunbooksBySupportTicketId } from "@/state/actions";

import { messages } from "./messages";
import { RunbookCard } from "./runbook-card";

export type MigrationRunbooksPanelProps = {
    supportTicketId: string;
    title: string;
    onClose: () => void;
};

const ticketContextStyles = xcss({
    paddingBlockEnd: "space.050"
});

const centeredStateStyles = xcss({
    paddingBlock: "space.300",
    paddingInline: "space.150"
});

const RunbookPanelLoadingState = () => (
    <Inline alignBlock="center" alignInline="center" xcss={centeredStateStyles}>
        <Spinner
            testId="spinner"
            interactionName="load"
            label="Loading"
            size="medium"
        />
    </Inline>
);

const RunbooksErrorState = ({ onRetry }: { onRetry: () => void }) => (
    <SectionMessage
        appearance="error"
        actions={[
            <SectionMessageAction key="retry-runbooks" onClick={onRetry}>
                <FormattedMessage {...messages.runbooksPanelRetryButton} />
            </SectionMessageAction>
        ]}
    >
        <Text>
            <FormattedMessage {...messages.runbooksPanelErrorMessage} />
        </Text>
    </SectionMessage>
);

export const MigrationRunbooksPanel: React.FC<MigrationRunbooksPanelProps> = ({
    supportTicketId,
    title,
    onClose
}) => {
    const intl = useIntl();
    const stargateClient = useBxpWebStargateClient();
    const dispatch = useFastshiftDispatch();
    const runbookSummaries = useFastshiftSelector(
        (state) => state.runbooks.summariesBySupportTicketId[supportTicketId]
    );
    const [expandedRunbookIds, setExpandedRunbookIds] = useState<Set<string>>(
        new Set()
    );
    const defaultExpandedSupportTicketId = useRef<string | null>(null);

    const fetchRunbookSummaries = useCallback(() => {
        dispatch(
            getFastshiftRunbooksBySupportTicketId({
                stargateClient,
                supportTicketId
            })
        );
    }, [dispatch, stargateClient, supportTicketId]);

    useEffect(() => {
        if (!runbookSummaries) {
            fetchRunbookSummaries();
        }
    }, [fetchRunbookSummaries, runbookSummaries]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [onClose]);

    useEffect(() => {
        const firstRunbookId = runbookSummaries?.data?.[0]?.runbookId;

        if (
            firstRunbookId &&
            defaultExpandedSupportTicketId.current !== supportTicketId
        ) {
            defaultExpandedSupportTicketId.current = supportTicketId;
            setExpandedRunbookIds(new Set([firstRunbookId]));
        }
    }, [runbookSummaries?.data, supportTicketId]);

    const toggleRunbook = useCallback((runbookId: string) => {
        setExpandedRunbookIds((previousIds) => {
            const nextIds = new Set(previousIds);
            if (nextIds.has(runbookId)) {
                nextIds.delete(runbookId);
            } else {
                nextIds.add(runbookId);
            }
            return nextIds;
        });
    }, []);

    const hasRunbooks = (runbookSummaries?.data?.length ?? 0) > 0;

    const runbooksContent = useMemo(() => {
        if (runbookSummaries?.pending) {
            return <RunbookPanelLoadingState />;
        }

        if (runbookSummaries?.error) {
            return <RunbooksErrorState onRetry={fetchRunbookSummaries} />;
        }

        if (runbookSummaries?.data && !hasRunbooks) {
            return (
                <Text size="small" color="color.text.subtle">
                    <FormattedMessage {...messages.runbooksPanelEmptyMessage} />
                </Text>
            );
        }

        if (runbookSummaries?.data) {
            return (
                <Stack space="space.150">
                    {runbookSummaries.data.map((summary) => (
                        <RunbookCard
                            key={summary.runbookId}
                            summary={summary}
                            supportTicketId={supportTicketId}
                            isExpanded={expandedRunbookIds.has(
                                summary.runbookId
                            )}
                            onToggle={() => toggleRunbook(summary.runbookId)}
                        />
                    ))}
                </Stack>
            );
        }

        return <RunbookPanelLoadingState />;
    }, [
        expandedRunbookIds,
        fetchRunbookSummaries,
        hasRunbooks,
        runbookSummaries?.data,
        runbookSummaries?.error,
        runbookSummaries?.pending,
        toggleRunbook
    ]);

    return (
        <Stack space="space.300" grow="fill">
            <Stack space="space.100">
                <Inline alignBlock="center" spread="space-between">
                    <Heading size="medium" as="h2">
                        <FormattedMessage {...messages.runbooksPanelTitle} />
                    </Heading>
                    <IconButton
                        icon={CrossIcon}
                        label={intl.formatMessage(
                            messages.runbooksPanelCloseButtonAriaLabel
                        )}
                        onClick={onClose}
                        testId="runbooks-panel-close-button"
                    />
                </Inline>
                <Box xcss={ticketContextStyles}>
                    <Stack space="space.050">
                        <Text size="small" color="color.text.subtle">
                            {supportTicketId}
                        </Text>
                        <Tooltip content={title}>
                            <Text maxLines={2}>{title}</Text>
                        </Tooltip>
                    </Stack>
                </Box>
            </Stack>
            {runbooksContent}
        </Stack>
    );
};
